import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslations } from "use-intl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TabsContent } from "@/components/ui/tabs";
import StatusTabs from "@/components/shared/StatusTabs";
import { Settings as SettingsIcon, RefreshCw } from "lucide-react";
import ReviewTable from "@/components/review/ReviewTable";
import ReviewDetailsModal from "@/components/review/ReviewDetailsModal";
import { ReviewItem } from "@/types/review";
import { ReviewApiClient } from "@/clients/reviewApiClient";
import { toast } from "sonner";
import { useCompany } from "@/hooks/useCompany";
import { formatAmount } from "@financialharmony/ui";

type ActionableFilter = "all" | "pending" | "needs_review";

// product-feedback-30-aug.F3 (issue #904) polling interval for the EC5 background-update
// resync while the Actionable tab is open.
const ACTIONABLE_POLL_MS = 8000;

const Review = () => {
  const t = useTranslations("consultantReview.page");
  // The latest translator, so a data-loading callback can word its error without reloading when the language changes.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const navigate = useNavigate();
  const { isPlatformAdminView } = useCompany();

  // Actionable = Pending ∪ Needs Review (product-feedback-30-aug.F3/AC4) — always fetched
  // together, never a silent duplicate of the Pending-only fetch.
  const [pendingItems, setPendingItems] = useState<ReviewItem[]>([]);
  const [needsReviewItems, setNeedsReviewItems] = useState<ReviewItem[]>([]);
  const [actionableLoading, setActionableLoading] = useState(true);
  const [actionableError, setActionableError] = useState(false);
  const [actionableUpdating, setActionableUpdating] = useState(false);
  const [actionableFilter, setActionableFilter] = useState<ActionableFilter>("all");

  const [approvedItems, setApprovedItems] = useState<ReviewItem[]>([]);
  const [completedLoaded, setCompletedLoaded] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<"actionable" | "completed">("actionable");
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // FIN-769 PR2: /review?suggestion=<id> opens that suggestion, from whichever list holds it.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSuggestionId = searchParams.get("suggestion");
  const [linkedOutcome, setLinkedOutcome] = useState<{ id: string; kind: "not_found" | "error" } | null>(null);
  const linkedProblem = linkedOutcome && linkedOutcome.id === linkedSuggestionId ? linkedOutcome.kind : null;

  const fetchActionable = useCallback(async (background = false) => {
    if (background) setActionableUpdating(true);
    else setActionableLoading(true);
    try {
      const [pending, needsReview] = await Promise.all([
        ReviewApiClient.getReviewItems(),
        ReviewApiClient.getNeedsManualReviewItems(),
      ]);
      setPendingItems(pending.items);
      setNeedsReviewItems(needsReview.items);
      setActionableError(false);
    } catch (error) {
      console.error("Error loading actionable review items:", error);
      setActionableError(true);
    } finally {
      if (background) setActionableUpdating(false);
      else setActionableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActionable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // product-feedback-30-aug.EC5: resync the Actionable list/badge in the background
  // while the tab is open, in case another consultant/process resolved an item.
  useEffect(() => {
    if (activeTab !== "actionable") return;
    const interval = setInterval(() => fetchActionable(true), ACTIONABLE_POLL_MS);
    return () => clearInterval(interval);
  }, [activeTab, fetchActionable]);

  const fetchCompleted = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && completedLoaded) return;
    setCompletedLoading(true);
    try {
      const result = await ReviewApiClient.getApprovedReviewItems();
      setApprovedItems(result.items);
      setCompletedLoaded(true);
    } catch (error) {
      console.error("Error loading completed review items:", error);
      toast.error(tRef.current("completedLoadFailed"));
    } finally {
      setCompletedLoading(false);
    }
  }, [completedLoaded]);

  useEffect(() => {
    if (activeTab === "completed") fetchCompleted();
  }, [activeTab, fetchCompleted]);

  // Each link is resolved once, after the Actionable lists first load; later polls never reopen it.
  const resolvedLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!linkedSuggestionId || actionableLoading) return;
    if (resolvedLinkRef.current === linkedSuggestionId) return;
    const linkId = linkedSuggestionId;
    resolvedLinkRef.current = linkId;
    const open = (item: ReviewItem) => {
      setSelectedItem(item);
      setModalOpen(true);
    };
    const loaded = [...pendingItems, ...needsReviewItems].find((i) => i.id === linkId);
    if (loaded) {
      open(loaded);
      return;
    }
    ReviewApiClient.getReviewItem(linkId)
      .then(({ item }) => {
        if (resolvedLinkRef.current !== linkId) return;
        if (item) open(item);
        else setLinkedOutcome({ id: linkId, kind: "not_found" });
      })
      .catch((error) => {
        if (resolvedLinkRef.current !== linkId) return;
        console.error("Error loading linked review item:", error);
        setLinkedOutcome({ id: linkId, kind: "error" });
      });
  }, [linkedSuggestionId, actionableLoading, pendingItems, needsReviewItems]);

  const handleModalOpenChange = (open: boolean) => {
    setModalOpen(open);
    if (!open && searchParams.has("suggestion")) {
      const next = new URLSearchParams(searchParams);
      next.delete("suggestion");
      setSearchParams(next, { replace: true });
    }
  };

  // FIN-1154 PE-R14: the opened suggestion goes in the address like a deep link, so the page event
  // names it (time per document). Marked resolved first, so the deep-link effect never refetches it.
  const handleReviewClick = (item: ReviewItem) => {
    resolvedLinkRef.current = item.id;
    setSelectedItem(item);
    setModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set("suggestion", item.id);
    setSearchParams(next, { replace: true });
  };

  const handleReviewComplete = () => {
    fetchActionable();
    if (completedLoaded) fetchCompleted(true);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return formatAmount(amount, currency);
  };

  const actionableItems = useMemo(
    () => [...pendingItems, ...needsReviewItems],
    [pendingItems, needsReviewItems],
  );
  const filteredActionableItems = useMemo(() => {
    if (actionableFilter === "pending") return pendingItems;
    if (actionableFilter === "needs_review") return needsReviewItems;
    return actionableItems;
  }, [actionableFilter, pendingItems, needsReviewItems, actionableItems]);

  const actionableCount = actionableError ? null : actionableItems.length;
  const completedCount = approvedItems.length;

  const filterChips: Array<{ value: ActionableFilter; label: string }> = [
    { value: "all", label: t("filters.all") },
    { value: "pending", label: t("filters.pending") },
    { value: "needs_review", label: t("filters.needsReview") },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-muted-foreground mt-2">{t("subtitle")}</p>
        </div>

        {/* Topbar with settings button */}
        {!isPlatformAdminView && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
            >
              <SettingsIcon className="h-4 w-4 mr-2" />
              {t("configureContext")}
            </Button>
          </div>
        )}
      </div>

      {linkedProblem && (
        <Alert variant={linkedProblem === "error" ? "destructive" : "default"}>
          <AlertDescription>
            {t(linkedProblem === "error" ? "linkedFailed" : "linkedNotFound")}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("cardTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <StatusTabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "actionable" | "completed")}
            tabs={[
              { value: "actionable", label: t("tabs.actionable"), count: actionableCount },
              { value: "completed", label: t("tabs.completed"), count: completedCount },
            ]}
          >
            <TabsContent value="actionable" className="mt-6 space-y-4">
              {actionableError ? (
                <div className="text-center py-8 space-y-3">
                  <p className="text-sm text-muted-foreground">{t("loadFailed")}</p>
                  <Button variant="outline" size="sm" onClick={() => fetchActionable()}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t("retry")}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    {filterChips.map((chip) => (
                      <Button
                        key={chip.value}
                        variant={actionableFilter === chip.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActionableFilter(chip.value)}
                      >
                        {chip.label}
                      </Button>
                    ))}
                    {actionableUpdating && (
                      <span className="text-xs text-muted-foreground">{t("updating")}</span>
                    )}
                  </div>

                  {actionableLoading ? (
                    <div className="text-center py-8">{t("loading")}</div>
                  ) : (
                    <ReviewTable
                      items={filteredActionableItems}
                      onReviewClick={handleReviewClick}
                      formatCurrency={formatCurrency}
                    />
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-6">
              {completedLoading ? (
                <div className="text-center py-8">{t("loading")}</div>
              ) : (
                <ReviewTable
                  items={approvedItems}
                  onReviewClick={handleReviewClick}
                  formatCurrency={formatCurrency}
                />
              )}
            </TabsContent>
          </StatusTabs>
        </CardContent>
      </Card>

      <ReviewDetailsModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        item={selectedItem}
        formatCurrency={formatCurrency}
        onReviewComplete={handleReviewComplete}
      />
    </div>
  );
};

export default Review;
